import type {ReactElement} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import type {AIGeneratedEmailFieldsResponse, EmailCampaignInputItemsProps} from './email-campaign-input-items.types';
import {EMAIL_CAMPAIGN_INPUT_ITEMS_REQUIRED_FIELDS, EmailCampaignInputItemTypes} from './email-campaign-input-items.types';
import styles from './email-campaign-input-items.module.scss';
import {InputField} from '@Components/input-field-v2';
import type {InputType} from '@Components/input-field-v2/input-field.types';
import {InputFieldType} from '@Components/input-field-v2/input-field.types';
import {EMAIL_CAMPAIGN_AI_CONTENT_API_PATH} from '@Libraries/ai-email-campaign-content-library';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './markdown-custom-style.scss';
import {EmojiPickerInput} from '@Components/emoji-picker-input';
import {Provider} from 'react-redux';
import {AiWriter} from '@Components/ai-writer';
import {AiWriterType} from '@Components/ai-writer/ai-writer.types';
import {useEmailCampaignContentWriter} from '@Components/ai-writer/adapters/use-email-campaign-content-writer';
import {formatUrl, isValidUrl} from '@Utils/url.util';

const EMAIL_INPUT_MAX_LENGTH_PX = {
  [EmailCampaignInputItemTypes.NAME]: 78,
  [EmailCampaignInputItemTypes.SUBJECT]: 78,
  [EmailCampaignInputItemTypes.PREHEADER]: 130,
  [EmailCampaignInputItemTypes.LINK_ON_DESIGN]: 2048,
};

export function EmailCampaignInputItems({
  name: initialName,
  emailCampaignId: emailCampaignId,
  subject: initialSubject,
  preheader: initialPreheader,
  textContent: initialTextContent,
  linkOnDesign: initialLinkOnDesign,
  isAiContentGenerationEnabled,
  autoAiGenerationActive,
  hasPosterId,
  hashedPosterId,
  ...props
}: EmailCampaignInputItemsProps): ReactElement<any> {
  const [inputValues, setInputValues] = useState<Record<EmailCampaignInputItemTypes, InputType | ReactQuill.Value>>({
    [EmailCampaignInputItemTypes.NAME]: initialName,
    [EmailCampaignInputItemTypes.SUBJECT]: initialSubject,
    [EmailCampaignInputItemTypes.PREHEADER]: initialPreheader,
    [EmailCampaignInputItemTypes.TEXT_CONTENT]: initialTextContent,
    [EmailCampaignInputItemTypes.LINK_ON_DESIGN]: initialLinkOnDesign,
  });
  const [errors, setErrors] = useState<Record<EmailCampaignInputItemTypes, null | string>>({
    [EmailCampaignInputItemTypes.NAME]: null,
    [EmailCampaignInputItemTypes.SUBJECT]: null,
    [EmailCampaignInputItemTypes.PREHEADER]: null,
    [EmailCampaignInputItemTypes.TEXT_CONTENT]: null,
    [EmailCampaignInputItemTypes.LINK_ON_DESIGN]: null,
  });

  const [isAutoGeneratingText, setIsAutoGeneratingText] = useState<boolean>(false);
  const subjectRef = useRef<HTMLTextAreaElement | null>(null);
  const preHeaderRef = useRef<HTMLTextAreaElement | null>(null);

  const getRandomizedLoadingMessages = (): {campaignNameLoading: string; subjectLoading: string; previewLoading: string} => {
    const randomNumber = Math.floor(Math.random() * 6) + 1;

    switch (randomNumber) {
      case 1:
        return {
          campaignNameLoading: window.i18next.t('pmwjs_publishing_campaign_name_loading_1'),
          subjectLoading: window.i18next.t('pmwjs_publishing_subject_line_loading_1'),
          previewLoading: window.i18next.t('pmwjs_publishing_preview_text_loading_1'),
        };
      case 2:
        return {
          campaignNameLoading: window.i18next.t('pmwjs_publishing_campaign_name_loading_2'),
          subjectLoading: window.i18next.t('pmwjs_publishing_subject_line_loading_2'),
          previewLoading: window.i18next.t('pmwjs_publishing_preview_text_loading_2'),
        };
      case 3:
        return {
          campaignNameLoading: window.i18next.t('pmwjs_publishing_campaign_name_loading_3'),
          subjectLoading: window.i18next.t('pmwjs_publishing_subject_line_loading_3'),
          previewLoading: window.i18next.t('pmwjs_publishing_preview_text_loading_3'),
        };
      case 4:
        return {
          campaignNameLoading: window.i18next.t('pmwjs_publishing_campaign_name_loading_4'),
          subjectLoading: window.i18next.t('pmwjs_publishing_subject_line_loading_4'),
          previewLoading: window.i18next.t('pmwjs_publishing_preview_text_loading_4'),
        };
      case 5:
        return {
          campaignNameLoading: window.i18next.t('pmwjs_publishing_campaign_name_loading_5'),
          subjectLoading: window.i18next.t('pmwjs_publishing_subject_line_loading_5'),
          previewLoading: window.i18next.t('pmwjs_publishing_preview_text_loading_5'),
        };
      default:
        return {
          campaignNameLoading: window.i18next.t('pmwjs_publishing_campaign_name_loading_6'),
          subjectLoading: window.i18next.t('pmwjs_publishing_subject_line_loading_6'),
          previewLoading: window.i18next.t('pmwjs_publishing_preview_text_loading_6'),
        };
    }
  };

  const {
    campaignNameLoading: randomizedCampaignNameLoadingStateMessage,
    subjectLoading: randomizedSubjectLinesLoadingStateMessage,
    previewLoading: randomizedPreviewTextLoadingStateMessage,
  } = getRandomizedLoadingMessages();

  const validateField = (field: EmailCampaignInputItemTypes, value: string): string | null => {
    if (EMAIL_CAMPAIGN_INPUT_ITEMS_REQUIRED_FIELDS.includes(field) && !value.trim()) {
      return window.i18next.t('pmwjs_publishing_field_required');
    }

    if (value.length > EMAIL_INPUT_MAX_LENGTH_PX[field as keyof typeof EMAIL_INPUT_MAX_LENGTH_PX]) {
      return window.i18next.t('pmwjs_publishing_max_length_exceeded');
    }

    return null;
  };

  const checkAllRequiredFieldsFilled = (): void => {
    let countOfFilledRequiredFields = 0;

    EMAIL_CAMPAIGN_INPUT_ITEMS_REQUIRED_FIELDS.forEach((field) => {
      const value = inputValues[field];
      const error = errors[field];

      if (value && !error) {
        countOfFilledRequiredFields = countOfFilledRequiredFields + 1;
      }
    });

    props.updateProgressBarWithFilledFields(EMAIL_CAMPAIGN_INPUT_ITEMS_REQUIRED_FIELDS.length, countOfFilledRequiredFields);
  };

  const handlePreheaderChange = (value: InputType): void => {
    const valueStr = value as string;
    const error = validateField(EmailCampaignInputItemTypes.PREHEADER, valueStr);

    setInputValues((prev) => ({
      ...prev,
      [EmailCampaignInputItemTypes.PREHEADER]: value,
    }));
    setErrors((prev) => ({...prev, [EmailCampaignInputItemTypes.PREHEADER]: error}));
    props.onEmailPreheaderChanged(valueStr);
  };

  const handleSubjectChange = (value: InputType): void => {
    const valueStr = value as string;
    const error = validateField(EmailCampaignInputItemTypes.SUBJECT, valueStr);

    setInputValues((prev) => ({
      ...prev,
      [EmailCampaignInputItemTypes.SUBJECT]: value,
    }));
    setErrors((prev) => ({...prev, [EmailCampaignInputItemTypes.SUBJECT]: error}));
    props.onEmailSubjectChanged(valueStr);
  };

  const handleNameChange = (value: InputType): void => {
    const valueStr = value as string;
    const error = validateField(EmailCampaignInputItemTypes.NAME, valueStr);

    setInputValues((prev) => ({
      ...prev,
      [EmailCampaignInputItemTypes.NAME]: value,
    }));
    setErrors((prev) => ({...prev, [EmailCampaignInputItemTypes.NAME]: error}));
  };

  const [rawLinkInput, setRawLinkInput] = useState<string>(initialLinkOnDesign || '');

  const handleLinkOnDesignChange = (value: InputType): void => {
    const valueStr = value as string;
    setRawLinkInput(valueStr);
    const lengthError = validateField(EmailCampaignInputItemTypes.LINK_ON_DESIGN, valueStr);

    setInputValues((prev) => ({
      ...prev,
      [EmailCampaignInputItemTypes.LINK_ON_DESIGN]: valueStr,
    }));

    setErrors((prev) => ({...prev, [EmailCampaignInputItemTypes.LINK_ON_DESIGN]: lengthError}));
  };

  const handleLinkOnDesignBlur = (): void => {
    const formattedUrl = formatUrl(rawLinkInput);

    let error = validateField(EmailCampaignInputItemTypes.LINK_ON_DESIGN, formattedUrl);

    if (!error && formattedUrl && !isValidUrl(formattedUrl)) {
      error = window.i18next.t('pmwjs_publishing_invalid_url');
    }

    setInputValues((prev) => ({
      ...prev,
      [EmailCampaignInputItemTypes.LINK_ON_DESIGN]: formattedUrl,
    }));

    setErrors((prev) => ({...prev, [EmailCampaignInputItemTypes.LINK_ON_DESIGN]: error}));

    if (!error) {
      props.onEmailLinkOnDesignChanged(formattedUrl);
    }
  };

  const handleTextContentChange = (value: ReactQuill.Value): void => {
    const valueStr = value as string;
    const error = validateField(EmailCampaignInputItemTypes.TEXT_CONTENT, valueStr);

    setInputValues((prev) => ({
      ...prev,
      [EmailCampaignInputItemTypes.TEXT_CONTENT]: value,
    }));
    setErrors((prev) => ({...prev, [EmailCampaignInputItemTypes.TEXT_CONTENT]: error}));
    props.onEmailTextContentChanged(valueStr);
  };

  const autoGenerateSubject = async (): Promise<void> => {
    if (!autoAiGenerationActive) {
      return;
    }

    setIsAutoGeneratingText(true);

    let response: {emailSubjects: AIGeneratedEmailFieldsResponse[]};

    try {
      if (hasPosterId) {
        response = (await window.PMW.readLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/getSubjectForPosterEmailCampaign`, {
          hashedposterid: hashedPosterId,
        })) as {emailSubjects: AIGeneratedEmailFieldsResponse[]};
      } else {
        response = (await window.PMW.readLocal(`${EMAIL_CAMPAIGN_AI_CONTENT_API_PATH}/getSubjectForTemplateEmailCampaign`, {
          idemailmarketingcampaign: emailCampaignId,
        })) as {emailSubjects: AIGeneratedEmailFieldsResponse[]};
      }

      const generatedContent = response.emailSubjects[0] ? response.emailSubjects[Math.floor(Math.random() * 4)] : null;

      if (generatedContent) {
        handleNameChange(generatedContent.campaignName);
        handleSubjectChange(generatedContent.subject);
        handlePreheaderChange(generatedContent.previewText);
      }
    } catch {
      /* empty */
    }

    setIsAutoGeneratingText(false);
    checkAllRequiredFieldsFilled();
  };

  useEffect(() => {
    props.onEmailSubjectChanged(inputValues[EmailCampaignInputItemTypes.SUBJECT] as string);
    props.onEmailPreheaderChanged(inputValues[EmailCampaignInputItemTypes.PREHEADER] as string);

    if (!inputValues[EmailCampaignInputItemTypes.SUBJECT] && autoAiGenerationActive) {
      void autoGenerateSubject();
    }
  }, []);

  useEffect(() => {
    checkAllRequiredFieldsFilled();
  }, [inputValues.subject, inputValues.name, errors]);

  useEffect(() => {
    setRawLinkInput(initialLinkOnDesign || '');
  }, [initialLinkOnDesign]);

  const onContentSelected = useCallback(
    (subject: string, previewText: string): void => {
      setInputValues((prev) => ({
        ...prev,
        [EmailCampaignInputItemTypes.SUBJECT]: subject,
        [EmailCampaignInputItemTypes.PREHEADER]: previewText,
      }));
      props.onEmailSubjectChanged(subject);
      props.onEmailPreheaderChanged(previewText);
    },
    [props.onEmailSubjectChanged, props.onEmailPreheaderChanged]
  );

  const aiWriterSource = useEmailCampaignContentWriter({onContentSelected});

  const renderAiWriterButton = (): ReactElement | null => {
    if (!isAiContentGenerationEnabled) {
      return null;
    }

    return (
      <div className="flexbox flex-row justify-content-end _full-width spacing-m-t-3">
        <Provider store={window.PMW.redux.store}>
          <AiWriter displayType={AiWriterType.POPOVER} source={aiWriterSource} closeOnItemSelect />
        </Provider>
      </div>
    );
  };

  const renderLinkOnDesignInput = (): ReactElement<any> | null => {
    if (!hasPosterId) {
      return null;
    }

    return (
      <div className="-old-publishing-flow spacing-p-l-2 spacing-p-r-2 js-blurable-in-ai-mode spacing-m-t-3">
        <label className="js-blurable-in-ai-mode body-sm-bold">{window.i18next.t('pmwjs_publishing_link_on_design')}</label>
        <span className="body-md-regular content-disabled js-blurable-in-ai-mode spacing-m-l-1">({window.i18next.t('pmwjs_publishing_optional')})</span>
        <InputField
          maxLength={2048}
          input={inputValues[EmailCampaignInputItemTypes.LINK_ON_DESIGN] as InputType}
          className={`js-email-link-on-design ${errors[EmailCampaignInputItemTypes.LINK_ON_DESIGN] ? styles.error : ''}`}
          placeholder="https://example..."
          onInputChange={handleLinkOnDesignChange}
          onInputBlur={handleLinkOnDesignBlur}
        />
        {errors[EmailCampaignInputItemTypes.LINK_ON_DESIGN] ? (
          <div className="body-sm-bold text-danger spacing-m-t-1">{errors[EmailCampaignInputItemTypes.LINK_ON_DESIGN]}</div>
        ) : null}
      </div>
    );
  };

  const renderPosterEmailCampaignElements = (): ReactElement<any> | null => {
    if (!hasPosterId) {
      return null;
    }

    return (
      <>
        {renderLinkOnDesignInput()}

        <div className="spacing-p-l-2 spacing-p-r-2 js-blurable-in-ai-mode spacing-m-t-3">
          <label className="js-blurable-in-ai-mode body-sm-bold">{window.i18next.t('pmwjs_publishing_text_content')}</label>
          <span className="body-md-regular content-disabled js-blurable-in-ai-mode spacing-m-l-1">({window.i18next.t('pmwjs_publishing_optional')})</span>

          <ReactQuill
            theme="snow"
            className={`${styles.container} spacing-m-b-3 ${errors[EmailCampaignInputItemTypes.TEXT_CONTENT] ? styles.error : ''}`}
            value={inputValues[EmailCampaignInputItemTypes.TEXT_CONTENT] as ReactQuill.Value}
            modules={{
              toolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                [{list: 'ordered'}, {list: 'bullet'}],
                [{header: 1}, {header: 2}],
                [{align: ['', 'center', 'right', 'justify']}],
              ],
            }}
            formats={['bold', 'italic', 'underline', 'strike', 'ordered', 'list', 'align', 'header']}
            onChange={handleTextContentChange}
          />
          {errors[EmailCampaignInputItemTypes.TEXT_CONTENT] ? (
            <div className="body-sm-bold text-danger spacing-m-t-1">{errors[EmailCampaignInputItemTypes.TEXT_CONTENT]}</div>
          ) : null}
        </div>
      </>
    );
  };

  const getEmojiFromLangString = (string?: string): string | undefined => {
    return string?.slice(0, 2);
  };

  const getTextFromLangString = (string?: string): string | undefined => {
    return string?.slice(2);
  };

  const renderCampaignNameLoader = (): ReactElement<any> | null => {
    if (!(autoAiGenerationActive && !inputValues[EmailCampaignInputItemTypes.NAME])) {
      return null;
    }

    if (!isAutoGeneratingText) {
      return null;
    }

    return (
      <div className={`${styles.campaignNameAILoader} ai-loader body-lg-bold`}>
        <span>{getEmojiFromLangString(randomizedCampaignNameLoadingStateMessage)}</span>
        <span className="ai-font-color">{getTextFromLangString(randomizedCampaignNameLoadingStateMessage)}</span>
      </div>
    );
  };

  const renderSubjectLineLoader = (): ReactElement<any> | null => {
    if (!(autoAiGenerationActive && !inputValues[EmailCampaignInputItemTypes.SUBJECT])) {
      return null;
    }

    if (!isAutoGeneratingText) {
      return null;
    }

    return (
      <div className="ai-loader body-lg-bold">
        <span>{getEmojiFromLangString(randomizedSubjectLinesLoadingStateMessage)}</span>
        <span className="ai-font-color">{getTextFromLangString(randomizedSubjectLinesLoadingStateMessage)}</span>
      </div>
    );
  };

  const renderPreviewTextLoader = (): ReactElement<any> | null => {
    if (!(autoAiGenerationActive && !inputValues[EmailCampaignInputItemTypes.PREHEADER] && !inputValues[EmailCampaignInputItemTypes.SUBJECT])) {
      return null;
    }

    if (!isAutoGeneratingText) {
      return null;
    }

    return (
      <div className="ai-loader body-lg-bold">
        <span>{getEmojiFromLangString(randomizedPreviewTextLoadingStateMessage)}</span>
        <span className="ai-font-color">{getTextFromLangString(randomizedPreviewTextLoadingStateMessage)}</span>
      </div>
    );
  };

  return (
    <>
      <div className={`spacing-p-l-2 spacing-p-r-2 js-blurable-in-ai-mode ${hasPosterId ? '-old-publishing-flow' : ''}`}>
        <div className="_position-relative">
          {renderCampaignNameLoader()}
          <div className="_position-relative">
            <InputField
              maxLength={EMAIL_INPUT_MAX_LENGTH_PX[EmailCampaignInputItemTypes.NAME]}
              input={inputValues[EmailCampaignInputItemTypes.NAME] as InputType}
              data-required-input-filled={inputValues[EmailCampaignInputItemTypes.NAME] ? '1' : undefined}
              className={`js-email-campaign-name body-lg-regular ${errors[EmailCampaignInputItemTypes.NAME] ? styles.error : ''}`}
              placeholder={autoAiGenerationActive && isAutoGeneratingText ? ' ' : window.i18next.t('pmwjs_publishing_name')}
              onInputChange={handleNameChange}
              label={window.i18next.t('pmwjs_publishing_campaign_name')}
            />
          </div>
        </div>
        {errors[EmailCampaignInputItemTypes.NAME] ? <div className="body-sm-bold text-danger spacing-m-t-1">{errors[EmailCampaignInputItemTypes.NAME]}</div> : null}
      </div>

      <div className="border-s-darker radius-8 spacing-p-4 spacing-m-l-2 spacing-m-r-2 spacing-m-t-3 js-blurable-in-ai-mode">
        <div className="_position-relative -emoji-container js-subject-container">
          {renderSubjectLineLoader()}
          <div className="_position-relative">
            <div className={styles.emojiContainer}>
              <EmojiPickerInput textAreaRef={subjectRef} onEmojiSelectCallback={handleSubjectChange} />
            </div>
            <InputField
              ref={subjectRef}
              maxLength={EMAIL_INPUT_MAX_LENGTH_PX[EmailCampaignInputItemTypes.SUBJECT]}
              input={inputValues[EmailCampaignInputItemTypes.SUBJECT] as InputType}
              className={`js-email-subject-input body-lg-regular ${errors[EmailCampaignInputItemTypes.SUBJECT] ? styles.error : ''}`}
              placeholder={window.i18next.t('pmwjs_publishing_subject_placeholder')}
              onInputChange={handleSubjectChange}
              label={window.i18next.t('pmwjs_publishing_subject')}
              inputType={InputFieldType.TEXT}
              isMultiline
            />
          </div>
          {errors[EmailCampaignInputItemTypes.SUBJECT] ? <div className="body-sm-bold text-danger spacing-m-t-1">{errors[EmailCampaignInputItemTypes.SUBJECT]}</div> : null}
        </div>

        <div className="_position-relative js-preview-text-container spacing-m-t-2">
          {renderPreviewTextLoader()}
          <label className="js-blurable-in-ai-mode body-sm-bold">{window.i18next.t('pmwjs_publishing_preview_text_simple')}</label>
          <span className="body-md-regular content-disabled js-blurable-in-ai-mode spacing-m-l-1">({window.i18next.t('pmwjs_publishing_optional')})</span>
          <div className="_position-relative">
            <div className={styles.emojiContainer}>
              <EmojiPickerInput textAreaRef={preHeaderRef} onEmojiSelectCallback={handlePreheaderChange} />
            </div>
            <InputField
              ref={preHeaderRef}
              maxLength={EMAIL_INPUT_MAX_LENGTH_PX[EmailCampaignInputItemTypes.PREHEADER]}
              input={inputValues[EmailCampaignInputItemTypes.PREHEADER] as InputType}
              className={`js-email-previewtext-input body-lg-regular ${errors[EmailCampaignInputItemTypes.PREHEADER] ? styles.error : ''}`}
              placeholder={window.i18next.t('pmwjs_publishing_preview_text_placeholder')}
              onInputChange={handlePreheaderChange}
              isMultiline
            />
          </div>
          {errors[EmailCampaignInputItemTypes.PREHEADER] ? <div className="body-sm-bold text-danger spacing-m-t-1">{errors[EmailCampaignInputItemTypes.PREHEADER]}</div> : null}
        </div>

        {renderAiWriterButton()}
      </div>
      {renderPosterEmailCampaignElements()}
    </>
  );
}
